import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { IconSprite } from './ui/Icon';
import { initTheme } from './hooks/useTheme';
import AdminLayout from './components/layout/AdminLayout';
import LandingPage from './pages/LandingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import TenantsPage from './pages/TenantsPage';
import NewTenantsPage from './pages/NewTenantsPage';
import PptContentPage from './pages/PptContentPage';
import UnitsPage from './pages/UnitsPage';
import BpayMappingPage from './pages/BpayMappingPage';
import CompletedWorksPage from './pages/CompletedWorksPage';
import ExpensesPage from './pages/ExpensesPage';
import OfficialDocsPage from './pages/OfficialDocsPage';
import PetsPage from './pages/PetsPage';
import MetersPage from './pages/MetersPage';
import InvoicesPage from './pages/InvoicesPage';
import PaymentsHub from './pages/PaymentsHub';
import QrPrintPreviewPage from './pages/QrPrintPreviewPage';
import ReconciliationPage from './pages/ReconciliationPage';
import GovernancePage from './pages/GovernancePage';
import AdminUsersPage from './pages/AdminUsersPage';
import BoardPage from './pages/BoardPage';
import ResidentsPage from './pages/ResidentsPage';
import NotificationsPage from './pages/NotificationsPage';
import SmsPage from './pages/SmsPage';
import InvitationsPage from './pages/InvitationsPage';
import SettingsPage from './pages/SettingsPage';
import PhoneDirectoryPage from './pages/PhoneDirectoryPage';
import MaintenancePage from './pages/MaintenancePage';
import DocumentsPage from './pages/DocumentsPage';
import ReportsPage from './pages/ReportsPage';
import ContactsPage from './pages/ContactsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import TariffsPage from './pages/TariffsPage';
import AssetsPage from './pages/AssetsPage';
import BudgetPage from './pages/BudgetPage';
import ParkingPage from './pages/ParkingPage';
import DistrictPage from './pages/DistrictPage';
import EmployeesPage from './pages/EmployeesPage';
import HoaRegistrationPage from './pages/HoaRegistrationPage';
import AccessControlPage from './pages/AccessControlPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PublicUnitPage from './pages/PublicUnitPage';
import EbarimtPage from './pages/EbarimtPage';
import AccountingPage from './pages/AccountingPage';

initTheme();

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>Уншиж байна...</div>;
  if (!isAuthenticated) return <Navigate to="/welcome" replace />;
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  const target = user?.role === 'district' ? '/district' : '/dashboard';
  return <Navigate to={target} replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  return (
    <Routes>
      <Route path="/welcome" element={isAuthenticated ? <DefaultRedirect /> : <LandingPage />} />
      <Route path="/login" element={isAuthenticated ? <DefaultRedirect /> : <AuthPage initialTab="login" />} />
      <Route path="/login-password" element={<Navigate to="/login" replace />} />
      <Route path="/register" element={isAuthenticated ? <DefaultRedirect /> : <AuthPage initialTab="register" />} />
      <Route path="/how" element={<HowItWorksPage />} />
      <Route path="/hoa-register" element={<HoaRegistrationPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/u/:token" element={<PublicUnitPage />} />
      <Route path="/" element={<RequireAuth><AdminLayout /></RequireAuth>}>
        <Route index element={<DefaultRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="tenants" element={<TenantsPage />} />
        <Route path="new-tenants" element={<NewTenantsPage />} />
        <Route path="ppt-content" element={<PptContentPage />} />
        <Route path="units" element={<UnitsPage />} />
        <Route path="units/bpay-mapping" element={<BpayMappingPage />} />
        <Route path="qr-print-preview" element={<QrPrintPreviewPage />} />
        <Route path="pets" element={<PetsPage />} />
        <Route path="meters" element={<MetersPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="payments" element={<PaymentsHub />} />
        <Route path="reconciliation" element={<ReconciliationPage />} />
        <Route path="governance" element={<GovernancePage />} />
        <Route path="admin-users" element={<AdminUsersPage />} />
        <Route path="board" element={<BoardPage />} />
        <Route path="residents" element={<ResidentsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="sms" element={<SmsPage />} />
        <Route path="invitations" element={<InvitationsPage />} />
        <Route path="maintenance" element={<MaintenancePage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="official-docs" element={<OfficialDocsPage />} />
        <Route path="works" element={<CompletedWorksPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="tariffs" element={<TariffsPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="budget" element={<BudgetPage />} />
        <Route path="parking" element={<ParkingPage />} />
        <Route path="access-control" element={<AccessControlPage />} />
        <Route path="district" element={<DistrictPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="phone-directory" element={<PhoneDirectoryPage />} />
        <Route path="ebarimt" element={<EbarimtPage />} />
        <Route path="accounting" element={<AccountingPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <IconSprite />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
